import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ExternalLink, Pencil, Plus, Trash2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { aapi, apiErr, can, useA, useAdminApi, useAuth, CONTENT } from "./lib";
import { SCHEMAS } from "./schemas";
import { FileField, ImagesField, LatLngField, MediaListField, RichText } from "./Widgets";
import { Sel } from "../components/Fields";

const lbl = "mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-ivory/55";

function useOptions() {
  const { tr } = useI18n();
  const { categories, locations } = useSite();
  const agents = useAdminApi("/admin/r/agents?page_size=200");
  const { user } = useAuth();
  const projects = useAdminApi(can(user, CONTENT) ? "/admin/r/projects?page_size=200" : null);
  return {
    cat: (kind) => categories.filter((c) => c.kind === kind).map((c) => [c.key, tr(c, "label")]),
    cities: locations.map((l) => [l.slug, tr(l, "name")]),
    nbhd: (city) => (locations.find((l) => l.slug === city)?.neighborhoods || []).map((n) => [n.slug, tr(n, "name")]),
    agents: (agents.data?.items || []).map((x) => [x.id, tr(x, "name")]),
    projects: (projects.data?.items || []).map((x) => [x.id, tr(x, "title")]),
  };
}

function Field({ fd, v, set, o, ro, tid, doc, Nested }) {
  const a = useA();
  const val = v[fd.k];
  const put = (x) => set({ [fd.k]: x });
  const S = (options, value = val, cb = put, any = "—") => <Sel testid={tid} value={value ?? ""} onChange={(x) => cb(x === "" ? null : x)} placeholder={any} options={options} />;
  switch (fd.t) {
    case "text": return <input data-testid={tid} className="field !py-2.5" value={val ?? ""} onChange={(e) => put(e.target.value)} disabled={ro} />;
    case "ro": return <p className="text-sm text-ivory/50" dir="ltr">{val || "—"}</p>;
    case "textarea": return <textarea data-testid={tid} className="field min-h-[90px]" value={val ?? ""} onChange={(e) => put(e.target.value)} disabled={ro} />;
    case "num": return <input data-testid={tid} type="number" step="any" className="field !py-2.5" dir="ltr" value={val ?? ""} onChange={(e) => put(e.target.value === "" ? null : Number(e.target.value))} disabled={ro} />;
    case "date": return <input data-testid={tid} type="date" className="field !py-2.5 [color-scheme:dark]" value={(val || "").slice(0, 10)} onChange={(e) => put(e.target.value || null)} disabled={ro} />;
    case "bool": return <input data-testid={tid} type="checkbox" className="h-5 w-5 accent-[#C9A96E]" checked={!!val} onChange={(e) => put(e.target.checked)} disabled={ro} />;
    case "select": return S(fd.opts.filter(([k]) => k !== ""));
    case "catOne": return S(o.cat(fd.kind));
    case "city": return S(o.cities, val, (x) => set({ city: x, ...(doc.neighborhood !== undefined && fd.k === "city" ? { neighborhood: o.nbhd(x)[0]?.[0] || "" } : {}) }));
    case "nbhd": return S(o.nbhd(v.city));
    case "agent": return S(o.agents, val, put, a("Unassigned", "غير معيّن"));
    case "project": return S(o.projects, val, put, a("None", "لا يوجد"));
    case "multi": {
      const cur = val || [];
      return <div className="flex flex-wrap gap-1.5" data-testid={tid}>{fd.opts.map(([k, l]) => (<button key={k} type="button" disabled={ro} onClick={() => put(cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k])} className={`chip !py-1 ${cur.includes(k) ? "chip-on" : ""}`} data-testid={`${tid}-${k}`}>{l}</button>))}</div>;
    }
    case "cat": {
      const cur = val || [];
      return <div className="flex flex-wrap gap-1.5" data-testid={tid}>{o.cat(fd.kind).map(([k, l]) => (<button key={k} type="button" disabled={ro} onClick={() => put(cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k])} className={`chip !py-1 ${cur.includes(k) ? "chip-on" : ""}`}>{l}</button>))}</div>;
    }
    case "tags": return <input data-testid={tid} className="field !py-2.5" placeholder="a, b, c" value={(val || []).join(", ")} onChange={(e) => put(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} disabled={ro} />;
    case "images": return <ImagesField value={val || []} onChange={put} testid={tid} disabled={ro} />;
    case "file": return <FileField kind={fd.kind} value={val} onChange={put} testid={tid} disabled={ro} />;
    case "media": return <MediaListField value={val || []} onChange={put} testid={tid} />;
    case "latlng": return <LatLngField lat={v.lat} lng={v.lng} onChange={(lat, lng) => set({ lat, lng })} disabled={ro} testid={tid} />;
    case "kv": case "flags": return (
      <div className="grid gap-2 sm:grid-cols-2">{fd.keys.map((k) => (
        <label key={k} className="flex items-center gap-2 text-sm text-ivory/70"><span className="w-28 shrink-0">{k}</span>
          {fd.t === "kv" ? <input className="field !py-2" dir="ltr" value={val?.[k] || ""} onChange={(e) => put({ ...val, [k]: e.target.value })} data-testid={`${tid}-${k}`} />
            : <input type="checkbox" className="h-4 w-4 accent-[#C9A96E]" checked={!!val?.[k]} onChange={(e) => put({ ...val, [k]: e.target.checked })} data-testid={`${tid}-${k}`} />}
        </label>))}</div>
    );
    case "seo": return (
      <div className="grid gap-3 sm:grid-cols-2">
        {[["title_en", "SEO title (EN)"], ["title_ar", "SEO title (AR)"], ["description_en", "Meta description (EN)"], ["description_ar", "Meta description (AR)"]].map(([k, l]) => (
          <div key={k}><span className={lbl}>{l}</span><input className="field !py-2" dir={k.endsWith("ar") ? "rtl" : "ltr"} value={val?.[k] || ""} onChange={(e) => put({ ...val, [k]: e.target.value })} data-testid={`${tid}-${k}`} disabled={ro} /></div>
        ))}
        <div className="sm:col-span-2"><span className={lbl}>OG image</span><FileField kind="image" value={val?.og_image} onChange={(x) => put({ ...val, og_image: x })} testid={`${tid}-og`} disabled={ro} /></div>
      </div>
    );
    case "list": {
      const items = val || [];
      const upd = (i, patch) => put(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
      return (
        <div className="space-y-3" data-testid={tid}>
          {items.map((it, i) => (
            <div key={i} className="rounded-sm border border-ivory/10 bg-ink/40 p-4">
              <Nested fields={fd.sub} v={it} set={(p) => upd(i, p)} o={o} ro={ro} prefix={`${tid}-${i}`} doc={it} compact />
              {!ro && <button type="button" className="chip mt-3 !text-[#E4472B]" onClick={() => put(items.filter((_, j) => j !== i))} data-testid={`${tid}-${i}-remove`}><Trash2 className="h-3.5 w-3.5" />{a("Remove", "حذف")}</button>}
            </div>
          ))}
          {!ro && <button type="button" className="chip" onClick={() => put([...items, { ...fd.blank }])} data-testid={`${tid}-add`}><Plus className="h-3.5 w-3.5" />{a("Add item", "إضافة عنصر")}</button>}
        </div>
      );
    }
    default: return null;
  }
}

export function FormFields({ fields, v, set, o, ro, prefix, doc, compact }) {
  const a = useA();
  const wide = (t) => ["images", "list", "latlng", "seo", "rich", "kv", "flags", "media", "file"].includes(t);
  return (
    <div className={`grid gap-5 ${compact ? "sm:grid-cols-2" : "md:grid-cols-2"}`}>
      {fields.map((fd) => {
        const label = a(...fd.l);
        if (fd.bi) {
          return ["en", "ar"].map((lg) => {
            const k = `${fd.k}_${lg}`;
            const sub = { ...fd, k, bi: false };
            return (
              <div key={k} className={fd.t === "rich" ? "md:col-span-2" : ""}>
                <span className={lbl}>{label} ({lg.toUpperCase()})</span>
                {fd.t === "rich" ? <RichText value={v[k]} onChange={(x) => set({ [k]: x })} testid={`${prefix}-${k}`} dir={lg === "ar" ? "rtl" : "ltr"} />
                  : <div dir={lg === "ar" ? "rtl" : "ltr"}><Field fd={sub} v={v} set={set} o={o} ro={ro} tid={`${prefix}-${k}`} doc={doc} Nested={FormFields} /></div>}
              </div>
            );
          });
        }
        return (
          <div key={fd.k} className={wide(fd.t) ? (compact ? "sm:col-span-2" : "md:col-span-2") : ""}>
            <span className={lbl}>{label}</span>
            <Field fd={fd} v={v} set={set} o={o} ro={ro} tid={`${prefix}-${fd.k}`} doc={doc} Nested={FormFields} />
          </div>
        );
      })}
    </div>
  );
}

export function useOptionsHook() { return useOptions(); }

function AiDraftBar({ name, v, setV }) {
  const a = useA();
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const r = await aapi.post("/admin/ai/describe", { entity: name === "properties" ? "property" : "project", data: v });
      setV((s) => ({ ...s, ...r.data, ai_draft_pending: true }));
      toast.success(a("AI draft inserted — review, then approve and save.", "تم إدراج مسودة الذكاء الاصطناعي — راجعها ثم اعتمدها واحفظ."));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="glass mb-4 flex flex-wrap items-center gap-3 rounded-sm p-4" data-testid="ai-draft-bar">
      <button type="button" onClick={gen} disabled={busy} className="chip" data-testid="ai-generate-draft">{busy ? a("Generating…", "جارٍ الإنشاء…") : a("✦ Generate description draft (EN + AR)", "✦ إنشاء مسودة الوصف (EN + AR)")}</button>
      {v.ai_draft_pending && (<>
        <span data-testid="ai-draft-badge" className="rounded-sm bg-[#E4472B]/20 px-2 py-1 text-xs font-semibold text-[#E4472B]">{a("AI draft – needs review", "مسودة ذكاء اصطناعي – بحاجة لمراجعة")}</span>
        <button type="button" className="chip chip-on" onClick={() => setV((s) => ({ ...s, ai_draft_pending: false }))} data-testid="ai-approve-draft">{a("Approve draft", "اعتماد المسودة")}</button>
        <span className="text-xs text-ivory/50">{a("Publishing is blocked until approved.", "النشر غير متاح قبل الاعتماد.")}</span>
      </>)}
      <span className="text-xs text-ivory/40">{a("Uses only the specs you entered. Save after reviewing.", "يستخدم المواصفات المدخلة فقط. احفظ بعد المراجعة.")}</span>
    </div>
  );
}

export function ResourceEdit({ name }) {
  const a = useA();
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const sc = SCHEMAS[name];
  const o = useOptions();
  const isNew = id === "new";
  const [v, setV] = useState(isNew ? { ...sc.blank } : null);
  const [busy, setBusy] = useState(false);
  const ro = name === "properties" && user.role === "sales_agent";
  useEffect(() => { if (!isNew) aapi.get(`/admin/r/${name}/${id}`).then((r) => setV(r.data)).catch((e) => toast.error(apiErr(e))); }, [name, id, isNew]);
  if (!v) return <p className="text-ivory/50">…</p>;
  const save = async () => {
    setBusy(true);
    try {
      const r = isNew ? await aapi.post(`/admin/r/${name}`, v) : await aapi.put(`/admin/r/${name}/${id}`, v);
      toast.success(a("Saved", "تم الحفظ"));
      if (isNew) nav(`/admin/${name}/${r.data.id}`, { replace: true }); else setV(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`admin-edit-${name}`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div><Link to={`/admin/${name}`} className="text-xs text-gold">← {a(...sc.title)}</Link><h1 className="h-display mt-1 text-3xl">{isNew ? a("New", "جديد") : v.title_en || v.name_en || v.name || v.slug || v.key}</h1></div>
        <div className="flex gap-2">
          {!isNew && sc.view && <a href={sc.view(v)} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2.5" data-testid="admin-edit-view-public"><ExternalLink className="h-4 w-4" />{a("View", "عرض")}</a>}
          {!ro && <button type="button" onClick={save} disabled={busy} className="btn-gold !py-2.5" data-testid="admin-edit-save">{busy ? "…" : a("Save", "حفظ")}</button>}
        </div>
      </div>
      {ro && <p className="mb-4 text-sm text-gold">{a("Read-only access", "صلاحية قراءة فقط")}</p>}
      {!ro && ["properties", "projects"].includes(name) && <AiDraftBar name={name} v={v} setV={setV} />}
      <div className="glass rounded-sm p-5 md:p-8"><FormFields fields={sc.fields} v={v} set={(p) => setV((s) => ({ ...s, ...p }))} o={o} ro={ro} prefix={`f-${name}`} doc={v} /></div>
    </div>
  );
}

export function ResourceList({ name }) {
  const a = useA();
  const { tr } = useI18n();
  const { user } = useAuth();
  const sc = SCHEMAS[name];
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const { data, reload } = useAdminApi(`/admin/r/${name}?page_size=200${query ? `&q=${encodeURIComponent(query)}` : ""}`);
  const ro = name === "properties" && user.role === "sales_agent";
  const toggle = async (d, k) => {
    try { await aapi.put(`/admin/r/${name}/${d.id}`, { ...d, [k]: !d[k] }); toast.success(`${k}: ${!d[k]}`); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (d) => {
    if (!window.confirm(a("Delete this item permanently?", "حذف هذا العنصر نهائياً؟"))) return;
    try { await aapi.delete(`/admin/r/${name}/${d.id}`); toast.success(a("Deleted", "تم الحذف")); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  const dup = async (d) => { try { await aapi.post(`/admin/r/${name}/${d.id}/duplicate`); toast.success(a("Duplicated (unpublished)", "تم النسخ (غير منشور)")); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const cell = (d, c) => (c === "title_en" || c === "name_en" ? tr(d, c.replace("_en", "")) : String(d[c] ?? "—"));
  return (
    <div data-testid={`admin-list-${name}`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="h-display text-3xl">{a(...sc.title)} <span className="text-base text-ivory/40">({data?.total ?? "…"})</span></h1>
        <div className="flex gap-2">
          <form onSubmit={(e) => { e.preventDefault(); setQuery(q); }}><input className="field !w-56 !py-2.5" placeholder={a("Search…", "بحث…")} value={q} onChange={(e) => setQ(e.target.value)} data-testid={`admin-${name}-search`} /></form>
          {!ro && <Link to={`/admin/${name}/new`} className="btn-gold !py-2.5" data-testid={`admin-${name}-new`}><Plus className="h-4 w-4" />{a("New", "جديد")}</Link>}
        </div>
      </div>
      <div className="overflow-x-auto rounded-sm border border-ivory/10">
        <table className="w-full text-sm">
          <thead className="bg-ink-2 text-[11px] uppercase tracking-wider text-ivory/50">
            <tr>{sc.cols.map((c) => <th key={c} className="p-3 text-start">{c.replace("_en", "")}</th>)}{sc.toggles.map((t) => <th key={t} className="p-3">{t}</th>)}<th className="p-3" /></tr>
          </thead>
          <tbody>
            {data?.items.map((d) => (
              <tr key={d.id} className="border-t border-ivory/5 hover:bg-ivory/[0.02]" data-testid={`admin-row-${d.slug || d.key || d.id}`}>
                {sc.cols.map((c) => <td key={c} className="max-w-[280px] truncate p-3">{cell(d, c)}</td>)}
                {sc.toggles.map((t) => (<td key={t} className="p-3 text-center"><input type="checkbox" aria-label={t} className="h-4 w-4 accent-[#C9A96E]" checked={!!d[t]} disabled={ro} onChange={() => toggle(d, t)} data-testid={`admin-toggle-${t}-${d.slug || d.id}`} /></td>))}
                <td className="whitespace-nowrap p-3 text-end">
                  <Link to={`/admin/${name}/${d.id}`} className="inline-flex p-1.5 text-gold" aria-label="Edit" data-testid={`admin-edit-${d.slug || d.id}`}><Pencil className="h-4 w-4" /></Link>
                  {sc.dup && !ro && <button type="button" onClick={() => dup(d)} className="p-1.5 text-ivory/60 hover:text-gold" aria-label="Duplicate" data-testid={`admin-duplicate-${d.slug || d.id}`}><Copy className="h-4 w-4" /></button>}
                  {!ro && <button type="button" onClick={() => del(d)} className="p-1.5 text-ivory/60 hover:text-[#E4472B]" aria-label="Delete" data-testid={`admin-delete-${d.slug || d.id}`}><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
